import {
  type AttributeWidgetSource,
  isAttributeSource,
  type WidgetSource,
  WidgetSourceType,
} from '@neptune/shared/widgets-domain';

import {
  compileVegaLiteFloatSeriesQuery,
  decodeVegaLiteFloatSeriesOptions,
} from './vega-lite-float-series-compiler';
import type {
  FloatSeriesDependencySource,
  VegaLiteFloatSeriesOptionsV2,
} from './vega-lite-float-series-types';
import { cloneJsonObject, isJsonObject, type JsonObject, type JsonValue } from './vega-lite-json';
import { assertVegaLiteJsonTree, validateVegaLiteJsonTree } from './vega-lite-json-tree';
import { VEGA_LITE_SCHEMA_URL } from './vega-lite-spec-policy';
import {
  type EvaluationField,
  isEvaluationFieldType,
  isVegaLiteDataFormat,
  normalizeVegaLiteWidgetOptions,
  VEGA_LITE_DATASET_NAME,
  VEGA_LITE_OPTIONS_VERSION,
  type VegaLiteDataFormat,
  type VegaLiteQuery,
  type VegaLiteWidgetOptions,
  type VegaLiteWidgetOptionsV1,
} from './vega-lite-widget-options';

/**
 * Attribute type of the single evaluation series a Vega-Lite widget may read.
 */
export const VEGA_LITE_CREATION_ATTRIBUTE_TYPE = 'evaluationSeries';

/**
 * The only source shape a creation document accepts: one exact evaluation-series attribute path,
 * without a subproperty, pattern, namespace or expression.
 */
export type VegaLiteCreationSource = {
  type: WidgetSourceType.ATTRIBUTE;
  value: string;
  metadata: {
    attributeType: typeof VEGA_LITE_CREATION_ATTRIBUTE_TYPE;
  };
};

/**
 * Draft-only authoring envelope for the JSON widget creator. It is never persisted and never added
 * to the API or DSL schema: it maps losslessly onto one widget source plus `VegaLiteWidgetOptionsV1`
 * and carries no widget id, name, project, run, endpoint, preview or editor state.
 *
 * `version` is the existing options version rather than a second independently versioned protocol.
 */
export type VegaLiteCreationDocumentV1 = {
  version: typeof VEGA_LITE_OPTIONS_VERSION;
  source: VegaLiteCreationSource;
  query: VegaLiteQuery;
  dataFormat: VegaLiteDataFormat;
  groupBy: EvaluationField[];
  spec: JsonObject;
};
export type VegaLiteCreationDocument = VegaLiteCreationDocumentV1 | VegaLiteFloatSeriesOptionsV2;

/**
 * A document whose `spec` is any JSON value. Authoring can produce one: a definition that parses as
 * JSON but is not an object has to reach the editor as written, so that the strict envelope check
 * can report it instead of the editor silently replacing it with the last valid definition.
 */
export type VegaLiteCreationDocumentDraft =
  | (Omit<VegaLiteCreationDocumentV1, 'spec'> & { spec: JsonValue })
  | (Omit<VegaLiteFloatSeriesOptionsV2, 'spec'> & { spec: JsonValue });

/** The widget-local pair a creation document maps onto. Everything else stays with the host. */
export type VegaLiteEvaluationWidgetConfiguration = {
  options: VegaLiteWidgetOptionsV1;
  source: AttributeWidgetSource;
};
export type VegaLiteFloatWidgetConfiguration = {
  options: VegaLiteFloatSeriesOptionsV2;
  sources: FloatSeriesDependencySource[];
};
export type VegaLiteWidgetConfiguration =
  | VegaLiteEvaluationWidgetConfiguration
  | VegaLiteFloatWidgetConfiguration;

/** JSON Pointer of the offending value. `/` denotes the whole document. */
export type VegaLiteCreationDocumentIssue = {
  message: string;
  path: string;
};

export type VegaLiteCreationDocumentDecodeResult =
  | { document: VegaLiteCreationDocument; status: 'valid' }
  | { issues: VegaLiteCreationDocumentIssue[]; status: 'invalidShape' };

/**
 * Text parsing keeps JSON syntax failures separate from shape failures so the editor can tell the
 * user that the document could not be read at all, instead of listing envelope rules for text that
 * never parsed.
 */
export type VegaLiteCreationDocumentParseResult =
  | VegaLiteCreationDocumentDecodeResult
  | { issue: VegaLiteCreationDocumentIssue; status: 'invalidSyntax' };

const ROOT_PATH = '/';

const ROOT_KEYS = ['version', 'source', 'query', 'dataFormat', 'groupBy', 'spec'] as const;
const SOURCE_KEYS = ['type', 'value', 'metadata'] as const;
const SOURCE_METADATA_KEYS = ['attributeType'] as const;
const CHART_QUERY_KEYS = ['kind', 'xMetricName', 'yMetricName'] as const;
const RECORDS_QUERY_KEYS = ['kind', 'metrics'] as const;
const FIELD_KEYS = ['name', 'type', 'as'] as const;

function formatKeyList(keys: readonly string[]): string {
  return keys.map((key) => `"${key}"`).join(', ');
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describeValue(value: unknown): string {
  if (value === null) {
    return 'null';
  }

  if (Array.isArray(value)) {
    return 'an array';
  }

  return `a ${typeof value}`;
}

type IssueCollector = {
  add(path: string, message: string): void;
  issues: VegaLiteCreationDocumentIssue[];
};

function createIssueCollector(): IssueCollector {
  const issues: VegaLiteCreationDocumentIssue[] = [];

  return {
    add(path, message) {
      issues.push({ message, path });
    },
    issues,
  };
}

function collectUnknownKeys({
  value,
  allowedKeys,
  ignoredKeys = [],
  path,
  label,
  collector,
}: {
  value: Record<string, unknown>;
  allowedKeys: readonly string[];
  ignoredKeys?: readonly string[];
  path: string;
  label: string;
  collector: IssueCollector;
}) {
  const unknownKeys = Object.keys(value).filter(
    (key) => !allowedKeys.includes(key) && !ignoredKeys.includes(key),
  );

  if (unknownKeys.length > 0) {
    collector.add(
      path,
      `Unsupported ${label} properties: ${formatKeyList(
        unknownKeys,
      )}. Supported properties: ${formatKeyList(allowedKeys)}.`,
    );
  }
}

export function createVegaLiteCreationSource(attributePath: string): VegaLiteCreationSource {
  return {
    type: WidgetSourceType.ATTRIBUTE,
    value: attributePath,
    metadata: { attributeType: VEGA_LITE_CREATION_ATTRIBUTE_TYPE },
  };
}

function decodeSource(
  value: unknown,
  collector: IssueCollector,
): VegaLiteCreationSource | undefined {
  if (!isPlainObject(value)) {
    collector.add('/source', `"source" must be an object; received ${describeValue(value)}.`);
    return undefined;
  }

  collectUnknownKeys({
    allowedKeys: SOURCE_KEYS,
    collector,
    label: 'source',
    path: '/source',
    value,
  });

  if (value.type !== WidgetSourceType.ATTRIBUTE) {
    collector.add(
      '/source/type',
      `"source.type" must be "${WidgetSourceType.ATTRIBUTE}"; a Vega-Lite widget reads exactly one evaluation series attribute.`,
    );
  }

  if (typeof value.value !== 'string') {
    collector.add(
      '/source/value',
      `"source.value" must be an evaluation series attribute path; received ${describeValue(
        value.value,
      )}.`,
    );
  } else if (value.value.length === 0) {
    collector.add('/source/value', 'Set the evaluation series attribute path.');
  }

  const metadata = value.metadata;

  if (!isPlainObject(metadata)) {
    collector.add(
      '/source/metadata',
      `"source.metadata" must be an object with "attributeType": "${VEGA_LITE_CREATION_ATTRIBUTE_TYPE}"; received ${describeValue(
        metadata,
      )}.`,
    );
    return undefined;
  }

  if ('subproperty' in metadata) {
    collector.add(
      '/source/metadata/subproperty',
      'A Vega-Lite widget source must not use a subproperty. Remove "source.metadata.subproperty".',
    );
  }

  collectUnknownKeys({
    allowedKeys: SOURCE_METADATA_KEYS,
    collector,
    // `subproperty` has its own message above, so it must not also be listed as an unknown key.
    ignoredKeys: ['subproperty'],
    label: 'source metadata',
    path: '/source/metadata',
    value: metadata,
  });

  if (metadata.attributeType !== VEGA_LITE_CREATION_ATTRIBUTE_TYPE) {
    collector.add(
      '/source/metadata/attributeType',
      `"source.metadata.attributeType" must be "${VEGA_LITE_CREATION_ATTRIBUTE_TYPE}".`,
    );
  }

  if (typeof value.value !== 'string' || value.value.length === 0) {
    return undefined;
  }

  if (
    value.type !== WidgetSourceType.ATTRIBUTE ||
    metadata.attributeType !== VEGA_LITE_CREATION_ATTRIBUTE_TYPE
  ) {
    return undefined;
  }

  return createVegaLiteCreationSource(value.value);
}

function decodeField({
  value,
  path,
  label,
  collector,
}: {
  value: unknown;
  path: string;
  label: string;
  collector: IssueCollector;
}): EvaluationField | undefined {
  if (!isPlainObject(value)) {
    collector.add(path, `Every ${label} must be an object; received ${describeValue(value)}.`);
    return undefined;
  }

  collectUnknownKeys({
    allowedKeys: FIELD_KEYS,
    collector,
    label,
    path,
    value,
  });

  const { as, name, type } = value;
  let isValid = true;

  if (typeof name !== 'string') {
    collector.add(`${path}/name`, `"name" must be a metric name; received ${describeValue(name)}.`);
    isValid = false;
  }

  if (!isEvaluationFieldType(type)) {
    collector.add(`${path}/type`, '"type" must be "FLOAT64" or "STRING".');
    isValid = false;
  }

  if (typeof as !== 'string') {
    collector.add(
      `${path}/as`,
      `"as" must be an output field name; received ${describeValue(as)}.`,
    );
    isValid = false;
  }

  if (
    !isValid ||
    typeof name !== 'string' ||
    typeof as !== 'string' ||
    !isEvaluationFieldType(type)
  ) {
    return undefined;
  }

  return { name, type, as };
}

function decodeFieldArray({
  value,
  path,
  label,
  collector,
}: {
  value: unknown;
  path: string;
  label: string;
  collector: IssueCollector;
}): EvaluationField[] | undefined {
  if (!Array.isArray(value)) {
    collector.add(path, `"${label}" must be an array; received ${describeValue(value)}.`);
    return undefined;
  }

  const fields = value.map((entry, index) =>
    decodeField({ collector, label: `${label} entry`, path: `${path}/${index}`, value: entry }),
  );

  return fields.every((field): field is EvaluationField => field !== undefined)
    ? fields
    : undefined;
}

function decodeQuery(value: unknown, collector: IssueCollector): VegaLiteQuery | undefined {
  if (!isPlainObject(value)) {
    collector.add('/query', `"query" must be an object; received ${describeValue(value)}.`);
    return undefined;
  }

  if (value.kind === 'chart') {
    collectUnknownKeys({
      allowedKeys: CHART_QUERY_KEYS,
      collector,
      label: 'chart query',
      path: '/query',
      value,
    });

    const { xMetricName, yMetricName } = value;

    if (typeof xMetricName !== 'string') {
      collector.add(
        '/query/xMetricName',
        `"xMetricName" must be a metric name; received ${describeValue(xMetricName)}.`,
      );
    }

    if (typeof yMetricName !== 'string') {
      collector.add(
        '/query/yMetricName',
        `"yMetricName" must be a metric name; received ${describeValue(yMetricName)}.`,
      );
    }

    if (typeof xMetricName !== 'string' || typeof yMetricName !== 'string') {
      return undefined;
    }

    return { kind: 'chart', xMetricName, yMetricName };
  }

  if (value.kind === 'records') {
    collectUnknownKeys({
      allowedKeys: RECORDS_QUERY_KEYS,
      collector,
      label: 'records query',
      path: '/query',
      value,
    });

    const metrics = decodeFieldArray({
      collector,
      label: 'metrics',
      path: '/query/metrics',
      value: value.metrics,
    });

    return metrics === undefined ? undefined : { kind: 'records', metrics };
  }

  collector.add(
    '/query/kind',
    '"query.kind" must be "chart" for chart-ready lines or "records" for raw evaluation records.',
  );

  return undefined;
}

function decodeVersion(value: unknown, collector: IssueCollector): boolean {
  if (value === VEGA_LITE_OPTIONS_VERSION) {
    return true;
  }

  collector.add(
    '/version',
    `"version" must be ${VEGA_LITE_OPTIONS_VERSION}; received ${JSON.stringify(value) ?? describeValue(value)}.`,
  );

  return false;
}

function decodeDataFormat(
  value: unknown,
  collector: IssueCollector,
): VegaLiteDataFormat | undefined {
  if (isVegaLiteDataFormat(value)) {
    return value;
  }

  collector.add(
    '/dataFormat',
    '"dataFormat" must be "wide" for one row per record or "long" for one row per value.',
  );

  return undefined;
}

function decodeSpec(value: unknown, collector: IssueCollector): JsonObject | undefined {
  if (isJsonObject(value)) {
    return cloneJsonObject(value);
  }

  if (isPlainObject(value)) {
    collector.add(
      '/spec',
      '"spec" must contain only JSON values. Remove functions, undefined and non-finite numbers.',
    );

    return undefined;
  }

  collector.add(
    '/spec',
    `"spec" must be a Vega-Lite definition object; received ${describeValue(value)}.`,
  );

  return undefined;
}

/**
 * Narrows an existing widget source to the creation contract. An incompatible source resolves to
 * `undefined` rather than being coerced, so a pasted or persisted pattern, namespace, expression or
 * subproperty source can be reported instead of silently becoming an evaluation series path.
 */
export function toVegaLiteCreationSource(
  source: WidgetSource | undefined,
): VegaLiteCreationSource | undefined {
  if (source === undefined || !isAttributeSource(source)) {
    return undefined;
  }

  if (
    source.metadata.attributeType !== VEGA_LITE_CREATION_ATTRIBUTE_TYPE ||
    source.metadata.subproperty !== undefined
  ) {
    return undefined;
  }

  return createVegaLiteCreationSource(source.value);
}

/**
 * Builds the authoring document for an existing configuration. Field order, aliases and the authored
 * definition are copied as they are: no dataset or schema binding is added here, because those are
 * runtime concerns of the renderer.
 */
export function createVegaLiteCreationDocument(input: {
  options: VegaLiteWidgetOptionsV1;
  source?: VegaLiteCreationSource;
}): VegaLiteCreationDocumentV1;
export function createVegaLiteCreationDocument(input: {
  options: VegaLiteFloatSeriesOptionsV2;
  source?: never;
}): VegaLiteFloatSeriesOptionsV2;
export function createVegaLiteCreationDocument(input: {
  options: VegaLiteWidgetOptions;
  source?: VegaLiteCreationSource;
}): VegaLiteCreationDocument;

export function createVegaLiteCreationDocument({
  options,
  source,
}: {
  options: VegaLiteWidgetOptions;
  source?: VegaLiteCreationSource;
}): VegaLiteCreationDocument {
  const normalized = normalizeVegaLiteWidgetOptions(options);

  if (normalized.version === 2) {
    return normalized;
  }

  return {
    version: VEGA_LITE_OPTIONS_VERSION,
    source: createVegaLiteCreationSource(source?.value ?? ''),
    query: normalized.query,
    dataFormat: normalized.dataFormat,
    groupBy: normalized.groupBy,
    spec: normalized.spec,
  };
}

/**
 * Starting point for a new widget: an empty source path, empty axis metric names and a small valid
 * point definition. Nothing is guessed from the runs the host has selected.
 */
export function createBlankVegaLiteCreationDocument(): VegaLiteCreationDocumentV1 {
  return {
    version: VEGA_LITE_OPTIONS_VERSION,
    source: createVegaLiteCreationSource(''),
    query: { kind: 'chart', xMetricName: '', yMetricName: '' },
    dataFormat: 'wide',
    groupBy: [],
    spec: {
      $schema: VEGA_LITE_SCHEMA_URL,
      data: { name: VEGA_LITE_DATASET_NAME },
      mark: { filled: true, type: 'point' },
      encoding: {
        x: { field: 'x', type: 'quantitative' },
        y: { field: 'y', type: 'quantitative' },
        color: { field: '_runLabel', type: 'nominal' },
      },
    },
  };
}

/**
 * Canonical text for the editor: known keys only, in the documented order, indented with two spaces.
 */
export function formatVegaLiteCreationDocument(document: VegaLiteCreationDocumentDraft): string {
  assertVegaLiteJsonTree(document);

  if (document.version === 2) {
    return JSON.stringify(document, null, 2);
  }

  return JSON.stringify(
    {
      version: document.version,
      source: {
        type: document.source.type,
        value: document.source.value,
        metadata: { attributeType: document.source.metadata.attributeType },
      },
      query: document.query,
      dataFormat: document.dataFormat,
      groupBy: document.groupBy,
      spec: document.spec,
    },
    null,
    2,
  );
}

/**
 * Strict, closed envelope check of an already parsed value. Every violation is reported with its
 * JSON Pointer so the editor can list them together, and unknown keys are rejected before the
 * existing option normalizers could silently drop them.
 *
 * Semantic incompleteness is deliberately left to the existing validators: a blank chart metric name
 * or an unknown metric is a shape-valid document that the configuration layer explains.
 */
export function decodeVegaLiteCreationDocument(
  value: unknown,
): VegaLiteCreationDocumentDecodeResult {
  const validation = validateVegaLiteJsonTree(value);

  if (validation.status === 'invalid') {
    return { issues: [validation.issue], status: 'invalidShape' };
  }

  if (isPlainObject(value) && value.version === 2) {
    const decoded = decodeVegaLiteFloatSeriesOptions(value);
    return decoded.status === 'valid'
      ? { status: 'valid', document: decoded.options }
      : { status: 'invalidShape', issues: decoded.issues };
  }

  const collector = createIssueCollector();

  if (!isPlainObject(value)) {
    collector.add(
      ROOT_PATH,
      `The creation document must be a JSON object; received ${describeValue(value)}.`,
    );

    return { issues: collector.issues, status: 'invalidShape' };
  }

  collectUnknownKeys({
    allowedKeys: ROOT_KEYS,
    collector,
    label: 'creation document',
    path: ROOT_PATH,
    value,
  });

  const hasVersion = decodeVersion(value.version, collector);
  const source = decodeSource(value.source, collector);
  const query = decodeQuery(value.query, collector);
  const dataFormat = decodeDataFormat(value.dataFormat, collector);
  const groupBy = decodeFieldArray({
    collector,
    label: 'groupBy',
    path: '/groupBy',
    value: value.groupBy,
  });
  const spec = decodeSpec(value.spec, collector);

  if (
    collector.issues.length > 0 ||
    !hasVersion ||
    source === undefined ||
    query === undefined ||
    dataFormat === undefined ||
    groupBy === undefined ||
    spec === undefined
  ) {
    return { issues: collector.issues, status: 'invalidShape' };
  }

  return {
    document: { version: VEGA_LITE_OPTIONS_VERSION, source, query, dataFormat, groupBy, spec },
    status: 'valid',
  };
}

/**
 * Parses editor text. A JSON syntax failure is a distinct result from a shape failure, because the
 * editor keeps the literal buffer in both cases but can only list envelope paths for the second.
 */
export function parseVegaLiteCreationDocument(text: string): VegaLiteCreationDocumentParseResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(text);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown parse error.';

    return {
      issue: { message: `The creation document is not valid JSON: ${message}`, path: ROOT_PATH },
      status: 'invalidSyntax',
    };
  }

  return decodeVegaLiteCreationDocument(parsed);
}

/**
 * Maps a shape-valid document onto the persisted widget contract: exactly one attribute source plus
 * supported version 1 options. Nothing outside `sources` and `options` is produced.
 */
export function mapVegaLiteCreationDocument(
  document: VegaLiteCreationDocumentV1,
): VegaLiteEvaluationWidgetConfiguration;
export function mapVegaLiteCreationDocument(
  document: VegaLiteFloatSeriesOptionsV2,
): VegaLiteFloatWidgetConfiguration;
export function mapVegaLiteCreationDocument(
  document: VegaLiteCreationDocument,
): VegaLiteWidgetConfiguration;

export function mapVegaLiteCreationDocument(
  document: VegaLiteCreationDocument,
): VegaLiteWidgetConfiguration {
  if (document.version === 2) {
    const compiled = compileVegaLiteFloatSeriesQuery(document.query);

    if (compiled.status === 'invalid') {
      throw new Error(compiled.issues.map(formatVegaLiteCreationDocumentIssue).join(' '));
    }

    return { options: normalizeVegaLiteWidgetOptions(document), sources: compiled.query.sources };
  }

  return {
    options: normalizeVegaLiteWidgetOptions({
      version: VEGA_LITE_OPTIONS_VERSION,
      query: document.query,
      dataFormat: document.dataFormat,
      groupBy: document.groupBy,
      spec: document.spec,
    }),
    source: createVegaLiteCreationSource(document.source.value),
  };
}

export function formatVegaLiteCreationDocumentIssue(issue: VegaLiteCreationDocumentIssue): string {
  return issue.path === ROOT_PATH ? issue.message : `${issue.path}: ${issue.message}`;
}
